export interface BusinessUnitOption {
	label: string
	value: number | string
	idProjects: number | string
	projectName: string
}

export const ALL_BU_OPTION: BusinessUnitOption = {
	label: 'All',
	value: 'all',
	idProjects: 'all',
	projectName: 'All',
}

export type UserBusinessContextPayload = {
	fullName: string | null
	fileName: string | null
	options: BusinessUnitOption[]
}

function getCookie(name: string): string | undefined {
	const value = `; ${document.cookie}`
	const parts = value.split(`; ${name}=`)
	return parts.pop()?.split(';').shift()
}

export function getRootDomain(): string {
	const host = window.location.host.split(':')[0]
	if (
		/^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
			host
		)
	) {
		return host
	}
	if (host.indexOf('localhost') !== -1) {
		return 'localhost'
	}
	const temp = host.split('.').reverse()
	return '.' + temp[1] + '.' + temp[0]
}

export function clearVmsSessionAndReload(): void {
	document.cookie =
		'MyToken=;path=/;domain=' + getRootDomain() + ';expires=' + new Date(0).toUTCString()
	window.location.reload()
}

/**
 * Loads profile + business unit options in one round-trip (same rules as header Topbar).
 */
export async function loadUserBusinessContext(): Promise<
	UserBusinessContextPayload | 'unauthorized' | 'no-token'
> {
	const token = getCookie('MyToken')
	if (!token) {
		return 'no-token'
	}
	const base = process.env.REACT_APP_API_URL || ''
	const headers: HeadersInit = {
		'Content-Type': 'application/json',
		Authorization: `Bearer ${token}`,
	}

	const [userRes, buRes] = await Promise.all([
		fetch(base + 'users/getUserInfo', { method: 'GET', headers }),
		fetch(base + 'users/businessUnits', { method: 'GET', headers }),
	])

	const userJson = await userRes.json().catch(() => ({}))
	if (!userRes.ok || userJson?.success === false || !userJson?.data) {
		return 'unauthorized'
	}

	const u = userJson.data
	const roleRows = Array.isArray(u.role_permission) ? u.role_permission : []
	const roleNames: string[] = roleRows
		.map((r: { roles?: { roleName?: string } }) => r?.roles?.roleName)
		.filter(Boolean)

	const buJson = await buRes.json().catch(() => ({}))
	const rawRows = Array.isArray(buJson?.data) ? buJson.data : []
	const mapped: BusinessUnitOption[] = rawRows.map(
		(el: { idProjects: number | string; projectName: string }) => ({
			...el,
			label: el.projectName,
			value: el.idProjects,
		})
	)

	let options = mapped
	if (roleNames.includes('Super Admin') || roleNames.includes('Admin')) {
		const hasAll = mapped.some((o) => o.idProjects === 'all' || o.value === 'all')
		if (!hasAll) {
			options = [ALL_BU_OPTION, ...mapped]
		}
	}

	return {
		fullName: u.fullName ?? null,
		fileName: u.fileName ?? null,
		options,
	}
}
